import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { auditLogger } from '@/utils/auditLogger'
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { EmailTemplateManager } from '@/components/superadmin/EmailTemplateManager'
import { 
  Settings as SettingsIcon, 
  Database, 
  Mail, 
  Shield, 
  Bell,
  Save,
  RefreshCw,
  Smartphone,
  CheckCircle,
  AlertTriangle,
  XCircle
} from 'lucide-react'

interface SystemSettings {
  app_name: string
  app_description: string
  maintenance_mode: boolean
  registration_enabled: boolean
  notification_enabled: boolean
  default_stamps_required: number
  support_email: string
  max_tenants: number
}

const SuperadminSettings = () => {
  const { isMaintenanceMode, setMaintenanceMode } = useMaintenanceMode()
  
  const [settings, setSettings] = useState<SystemSettings>({
    app_name: 'Herzensempfehlung',
    app_description: 'Digitales Treueprogramm für lokale Unternehmen',
    maintenance_mode: false,
    registration_enabled: true,
    notification_enabled: true,
    default_stamps_required: 10,
    support_email: 'support@herzensempfehlung.de',
    max_tenants: 100
  })
  
  const [loading, setLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [stats, setStats] = useState({
    totalTenants: 0,
    totalUsers: 0,
    totalStamps: 0,
    activeUsers: 0
  })
  
  // Profile state
  const [profile, setProfile] = useState({
    email: '',
    full_name: ''
  })
  const [profileLoading, setProfileLoading] = useState(false)
  
  // Wallet self-test state
  const [walletTestResults, setWalletTestResults] = useState<any>(null)
  const [walletTesting, setWalletTesting] = useState(false)
  
  const { toast } = useToast()

  // Sync maintenance mode with hook
  useEffect(() => {
    setSettings(prev => ({ ...prev, maintenance_mode: isMaintenanceMode }))
  }, [isMaintenanceMode])

  useEffect(() => {
    loadSettings()
    loadSystemStats()
    loadProfile()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      
      // In einer echten Implementierung würden Settings aus einer system_settings Tabelle geladen
      // Für jetzt verwenden wir Default-Werte
      
      await auditLogger.logDataAccess('system_settings', undefined, 'view')
    } catch (error: any) {
      console.error('Error loading settings:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Laden",
        description: "Einstellungen konnten nicht geladen werden."
      })
    } finally {
      setLoading(false)
    }
  }

  const loadProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('email, full_name')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single()

      if (error) throw error

      if (data) {
        setProfile({
          email: data.email || '',
          full_name: data.full_name || ''
        })
      }
    } catch (error) {
      console.error('Error loading profile:', error)
    }
  }

  const saveProfile = async () => {
    try {
      setProfileLoading(true)

      const { error } = await supabase
        .from('profiles')
        .update({
          email: profile.email,
          full_name: profile.full_name,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)

      if (error) throw error

      toast({
        title: "Profil aktualisiert",
        description: "Ihre Profilinformationen wurden erfolgreich gespeichert."
      })
    } catch (error: any) {
      console.error('Error saving profile:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Speichern",
        description: error.message || "Profil konnte nicht gespeichert werden."
      })
    } finally {
      setProfileLoading(false)
    }
  }

  const loadSystemStats = async () => {
    try {
      // Lade Statistiken für das System-Overview
      const [tenantsResult, usersResult, stampsResult] = await Promise.all([
        supabase.from('tenants').select('id', { count: 'exact' }),
        supabase.from('profiles').select('id', { count: 'exact' }),
        supabase.from('stamps').select('id', { count: 'exact' })
      ])

      // Aktive Benutzer (letzte 30 Tage)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      const { count: activeUsersCount } = await supabase
        .from('profiles')
        .select('id', { count: 'exact' })
        .gte('updated_at', thirtyDaysAgo)

      setStats({
        totalTenants: tenantsResult.count || 0,
        totalUsers: usersResult.count || 0,
        totalStamps: stampsResult.count || 0,
        activeUsers: activeUsersCount || 0
      })
    } catch (error) {
      console.error('Error loading system stats:', error)
    }
  }

  const saveSettings = async () => {
    try {
      setSaveLoading(true)

      // In einer echten Implementierung würden diese in eine system_settings Tabelle gespeichert
      // Für die Demo loggen wir nur die Änderungen
      
      await auditLogger.logAdminAction('update_system_settings', 'system_settings', undefined, {
        settings: settings
      })

      toast({
        title: "Einstellungen gespeichert",
        description: "Die Systemeinstellungen wurden erfolgreich aktualisiert."
      })
    } catch (error: any) {
      console.error('Error saving settings:', error)
      toast({
        variant: "destructive",
        title: "Fehler beim Speichern",
        description: error.message || "Einstellungen konnten nicht gespeichert werden."
      })
    } finally {
      setSaveLoading(false)
    }
  }

  const handleMaintenanceMode = async (enabled: boolean) => {
    const success = await setMaintenanceMode(enabled)
    
    if (success) {
      if (enabled) {
        toast({
          title: "Wartungsmodus aktiviert",
          description: "Das System ist jetzt im Wartungsmodus. Nur Administratoren können sich anmelden.",
          variant: "destructive"
        })
      } else {
        toast({
          title: "Wartungsmodus deaktiviert",
          description: "Das System ist wieder für alle Benutzer verfügbar."
        })
      }
    } else {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Wartungsmodus konnte nicht geändert werden."
      })
    }
  }

  const handleWalletSelfTest = async () => {
    setWalletTesting(true)
    setWalletTestResults(null)

    try {
      const { data, error } = await supabase.functions.invoke('wallet-setup-selftest')

      if (error) {
        throw error
      }

      setWalletTestResults(data)

      if (data.summary.overallStatus === 'success') {
        toast({
          title: "✅ Wallet-Setup erfolgreich",
          description: "Alle Tests bestanden. Apple Wallet Pässe sollten funktionieren.",
        })
      } else if (data.summary.overallStatus === 'warning') {
        toast({
          title: "⚠️ Wallet-Setup mit Warnungen",
          description: `${data.summary.warnings} Warnung(en) gefunden. Überprüfen Sie die Details.`,
          variant: "destructive",
        })
      } else {
        toast({
          title: "❌ Wallet-Setup fehlerhaft",
          description: `${data.summary.errors} Fehler gefunden. Zertifikate müssen korrigiert werden.`,
          variant: "destructive",
        })
      }

    } catch (error) {
      console.error('Wallet self-test error:', error)
      toast({
        title: "Fehler",
        description: `Selbsttest fehlgeschlagen: ${error.message}`,
        variant: "destructive",
      })
    } finally {
      setWalletTesting(false)
    }
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Systemeinstellungen</h2>
            <p className="text-muted-foreground">
              Verwalten Sie globale Systemkonfigurationen und Einstellungen
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={loadSystemStats}
              disabled={loading}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
            <Button onClick={saveSettings} disabled={saveLoading}>
              <Save className="mr-2 h-4 w-4" />
              {saveLoading ? 'Speichere...' : 'Speichern'}
            </Button>
          </div>
        </div>

        {/* System Overview */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unternehmen</CardTitle>
              <Database className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalTenants}</div>
              <p className="text-xs text-muted-foreground">
                von {settings.max_tenants} max.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Benutzer</CardTitle>
              <Shield className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUsers}</div>
              <p className="text-xs text-muted-foreground">
                {stats.activeUsers} aktiv (30 Tage)
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Stempel</CardTitle>
              <Badge className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalStamps.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">Gesamt vergeben</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Status</CardTitle>
              <SettingsIcon className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {settings.maintenance_mode ? (
                  <Badge variant="destructive">Wartung</Badge>
                ) : (
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Online</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Systemstatus</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 gap-1">
            <TabsTrigger value="profile" className="flex items-center gap-1 text-xs md:text-sm">
              <Shield className="h-4 w-4" />
              Profil
            </TabsTrigger>
            <TabsTrigger value="general" className="flex items-center gap-1 text-xs md:text-sm">
              <SettingsIcon className="h-4 w-4" />
              Allgemein
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center gap-1 text-xs md:text-sm">
              <Mail className="h-4 w-4" />
              E-Mail
            </TabsTrigger>
            <TabsTrigger value="wallet" className="flex items-center gap-1 text-xs md:text-sm">
              <Smartphone className="h-4 w-4" />
              Wallet
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Mein Profil
                </CardTitle>
                <CardDescription>
                  Bearbeiten Sie Ihre persönlichen Informationen
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="profile_name">Vollständiger Name</Label>
                  <Input
                    id="profile_name"
                    value={profile.full_name}
                    onChange={(e) => setProfile(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Ihr vollständiger Name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="profile_email">E-Mail Adresse</Label>
                  <Input
                    id="profile_email"
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="ihre@email.de"
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={saveProfile} disabled={profileLoading}>
                    <Save className="mr-2 h-4 w-4" />
                    {profileLoading ? 'Speichere...' : 'Profil speichern'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="general">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Allgemeine Einstellungen */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <SettingsIcon className="h-5 w-5" />
                    Allgemeine Einstellungen
                  </CardTitle>
                  <CardDescription>
                    Grundlegende Systemkonfiguration
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="app_name">Anwendungsname</Label>
                    <Input
                      id="app_name"
                      value={settings.app_name}
                      onChange={(e) => setSettings(prev => ({ ...prev, app_name: e.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="app_description">Beschreibung</Label>
                    <Textarea
                      id="app_description"
                      value={settings.app_description}
                      onChange={(e) => setSettings(prev => ({ ...prev, app_description: e.target.value }))}
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="support_email">Support E-Mail</Label>
                    <Input
                      id="support_email"
                      type="email"
                      value={settings.support_email}
                      onChange={(e) => setSettings(prev => ({ ...prev, support_email: e.target.value }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="max_tenants">Max. Unternehmen</Label>
                    <Input
                      id="max_tenants"
                      type="number"
                      min="1"
                      value={settings.max_tenants}
                      onChange={(e) => setSettings(prev => ({ ...prev, max_tenants: parseInt(e.target.value) || 100 }))}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* System-Kontrollen */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    System-Kontrollen
                  </CardTitle>
                  <CardDescription>
                    Aktivieren oder deaktivieren Sie Systemfunktionen
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Wartungsmodus</Label>
                      <p className="text-sm text-muted-foreground">
                        Blockiert den Zugang für normale Benutzer
                      </p>
                    </div>
                    <Switch
                      checked={settings.maintenance_mode}
                      onCheckedChange={handleMaintenanceMode}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Registrierung aktiviert</Label>
                      <p className="text-sm text-muted-foreground">
                        Erlaubt neuen Benutzern die Registrierung
                      </p>
                    </div>
                    <Switch
                      checked={settings.registration_enabled}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, registration_enabled: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Push-Benachrichtigungen</Label>
                      <p className="text-sm text-muted-foreground">
                        Systemweite Benachrichtigungen aktiviert
                      </p>
                    </div>
                    <Switch
                      checked={settings.notification_enabled}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, notification_enabled: checked }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="default_stamps">Standard Stempel-Anzahl</Label>
                    <Input
                      id="default_stamps"
                      type="number"
                      min="1"
                      max="50"
                      value={settings.default_stamps_required}
                      onChange={(e) => setSettings(prev => ({ ...prev, default_stamps_required: parseInt(e.target.value) || 10 }))}
                    />
                    <p className="text-sm text-muted-foreground">
                      Standard-Anzahl Stempel für neue Prämien
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* System Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  System-Aktionen
                </CardTitle>
                <CardDescription>
                  Erweiterte Systemverwaltung und Wartung
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  <Button variant="outline" className="h-auto p-4 flex flex-col items-start">
                    <Database className="h-6 w-6 mb-2" />
                    <div className="text-left">
                      <div className="font-medium">Datenbank bereinigen</div>
                      <div className="text-sm text-muted-foreground">Alte Audit-Logs entfernen</div>
                    </div>
                  </Button>

                  <Button variant="outline" className="h-auto p-4 flex flex-col items-start">
                    <Mail className="h-6 w-6 mb-2" />
                    <div className="text-left">
                      <div className="font-medium">E-Mail testen</div>
                      <div className="text-sm text-muted-foreground">Test-E-Mail versenden</div>
                    </div>
                  </Button>

                  <Button variant="outline" className="h-auto p-4 flex flex-col items-start">
                    <Bell className="h-6 w-6 mb-2" />
                    <div className="text-left">
                      <div className="font-medium">System-Benachrichtigung</div>
                      <div className="text-sm text-muted-foreground">An alle Benutzer senden</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="email">
            <EmailTemplateManager />
          </TabsContent>

          <TabsContent value="wallet">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Apple Wallet Konfiguration
                </CardTitle>
                <CardDescription>
                  Systemweite Wallet-Einstellungen und Tests
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    Testen Sie die Apple Wallet Konfiguration, um sicherzustellen, dass Ihre Zertifikate korrekt eingerichtet sind.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <Button 
                      onClick={handleWalletSelfTest}
                      disabled={walletTesting}
                      variant={walletTestResults?.summary?.overallStatus === 'success' ? 'default' : 'outline'}
                    >
                      <SettingsIcon className="h-4 w-4 mr-2" />
                      {walletTesting ? "Teste..." : "Wallet-Setup prüfen"}
                    </Button>
                    
                    {walletTestResults && (
                      <div className="flex items-center gap-2">
                        {walletTestResults.summary.overallStatus === 'success' && (
                          <Badge variant="default" className="bg-green-500">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Erfolgreich
                          </Badge>
                        )}
                        {walletTestResults.summary.overallStatus === 'warning' && (
                          <Badge variant="secondary">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Warnungen
                          </Badge>
                        )}
                        {walletTestResults.summary.overallStatus === 'error' && (
                          <Badge variant="destructive">
                            <XCircle className="h-3 w-3 mr-1" />
                            Fehler
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  {walletTestResults && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div className="p-4 border rounded-lg">
                          <div className="text-2xl font-bold text-green-600">{walletTestResults.summary.success}</div>
                          <div className="text-sm text-muted-foreground">Erfolgreich</div>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <div className="text-2xl font-bold text-yellow-600">{walletTestResults.summary.warnings}</div>
                          <div className="text-sm text-muted-foreground">Warnungen</div>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <div className="text-2xl font-bold text-red-600">{walletTestResults.summary.errors}</div>
                          <div className="text-sm text-muted-foreground">Fehler</div>
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-3">
                        <h3 className="font-semibold">Detaillierte Test-Ergebnisse:</h3>
                        
                        {walletTestResults.tests.map((test: any, index: number) => (
                          <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
                            <div className="mt-0.5">
                              {test.status === 'success' && <CheckCircle className="h-4 w-4 text-green-500" />}
                              {test.status === 'warning' && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
                              {test.status === 'error' && <XCircle className="h-4 w-4 text-red-500" />}
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="font-medium">{test.name}</div>
                              <div className="text-sm text-muted-foreground">{test.message}</div>
                              {test.details && (
                                <div className="text-xs bg-muted p-2 rounded mt-2">
                                  {test.details.suggestion && (
                                    <div className="font-medium mb-1">💡 Empfehlung: {test.details.suggestion}</div>
                                  )}
                                  {typeof test.details === 'object' && !test.details.suggestion && (
                                    <pre className="whitespace-pre-wrap">{JSON.stringify(test.details, null, 2)}</pre>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {walletTestResults.recommendations && walletTestResults.recommendations.length > 0 && (
                        <>
                          <Separator />
                          
                          <div className="space-y-3">
                            <h3 className="font-semibold">Empfehlungen:</h3>
                            {walletTestResults.recommendations.map((rec: string, index: number) => (
                              <Alert key={index}>
                                <AlertTriangle className="h-4 w-4" />
                                <AlertDescription>{rec}</AlertDescription>
                              </Alert>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <Separator />
                  
                  <div className="space-y-3">
                    <h3 className="font-semibold">Erforderliche Zertifikate:</h3>
                    <div className="text-sm text-muted-foreground space-y-2">
                      <div>• <strong>APPLE_PASS_TYPE_ID_CERTIFICATE:</strong> PKCS#12 Zertifikat (.p12) als Base64</div>
                      <div>• <strong>APPLE_PASS_CERTIFICATE_PASSWORD:</strong> Passwort für das PKCS#12 Zertifikat</div>
                      <div>• <strong>APPLE_WWDR_CERTIFICATE:</strong> Apple WWDR Zertifikat als Base64</div>
                      <div>• <strong>APPLE_PASS_TYPE_ID:</strong> Pass Type Identifier (z.B. pass.com.example.loyalty)</div>
                      <div>• <strong>APPLE_TEAM_ID:</strong> Apple Developer Team ID</div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mt-4">
                      Diese Zertifikate müssen über die Supabase Edge Functions Einstellungen konfiguriert werden.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  )
}

export default SuperadminSettings